import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress,
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { t } from 'i18next';
import { pushEmailClientSI, pushEmailCompanySI, pushEmailSIAll } from '../../../../actions/taskAction';
import { resetState } from '../../../../slices/taskSlice';

/**
 * SendEmailsDialog
 * A small dialog that requests the backend to send Shipping Instructions emails.
 * It calls one of three thunks: pushEmailClientSI, pushEmailCompanySI, pushEmailSIAll.
 * Shows inline success/error alerts and triggers toasts.
 */
export default function SendEmailsDialog({ open, onClose, row, taskType }) {
  const dispatch = useDispatch();
  const { loading, hasError, errorMessage } = useSelector((state) => state.task);

  // Auto-run when dialog opens with a specific taskType
  useEffect(() => {
    if (!open) return;
    if (!taskType) return;

    // run the action associated with the provided taskType
    switch (taskType) {
      case 'client':
        dispatch(pushEmailClientSI({ shipmentId: row?.id }));
        break;
      case 'company':
        dispatch(pushEmailCompanySI({ shipmentId: row?.id }));
        break;
      case 'all':
        dispatch(pushEmailSIAll({ shipmentId: row?.id }));
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, taskType, row]);

  useEffect(() => {
    return () => {
      dispatch(resetState());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        // prevent user from closing via backdrop click or escape
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose && onClose();
      }}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <EmailOutlinedIcon color="primary" />
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {t('Send Shipping Instructions')}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('Sending status for Shipping Instructions. This dialog is informational only.')}
          </Typography>
        </Box>

        {loading === 'pending' && <LinearProgress sx={{ mb: 1 }} />}

        {loading === 'succeeded' && (
          <Alert icon={<CheckCircleIcon />} severity="success" sx={{ mb: 1 }}>
            {t('Emails sent successfully')}
          </Alert>
        )}

        {(loading === 'failed' || hasError) && (
          <Alert icon={<ErrorOutlineIcon />} severity="error" sx={{ mb: 1 }}>
            {t('Failed to send emails')}: {errorMessage}
          </Alert>
        )}

      </DialogContent>

      <DialogActions>
        <Button onClick={() => onClose && onClose()} disabled={loading === 'pending'}>
          {t('close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
